import {
    BOX_TRUCK_DOC_TYPES,
    BOX_TRUCK_REGISTRATION_STRING_FIELDS,
    BoxTruckVerificationInfo,
    boxTruckVerificationDocs,
    BoxTruckVerificationPath,
} from '@numeo/types-core'

/** The operator-collected fields that count toward registration completeness (alongside the document types):
 *  the shared string set plus the numeric max-cargo-weight. MC is included even though it's "if active" — a
 *  driver without one just reads as less complete, fine for an at-a-glance progress signal. */
const COMPLETENESS_TEXT_FIELDS: readonly (keyof BoxTruckVerificationInfo)[] = [...BOX_TRUCK_REGISTRATION_STRING_FIELDS, 'maxCargoWeightLbs']

/** Percentage (0–100) of a driver's OPERATOR registration that's filled in — the text fields present + the
 *  document types uploaded, over the full expected set (COMPLETENESS_TEXT_FIELDS + BOX_TRUCK_DOC_TYPES). Drives
 *  the My Drivers progress column. Self-submitted verification packets use boxTruckVerificationCompleteness. */
export function boxTruckRegistrationCompleteness(info: BoxTruckVerificationInfo | undefined): number {
    if (!info) return 0
    let filled = 0
    for (const key of COMPLETENESS_TEXT_FIELDS) {
        const value = info[key]
        if (typeof value === 'number' ? Number.isFinite(value) : typeof value === 'string' && value.trim().length > 0) filled++
    }
    const uploaded = new Set((info.documents ?? []).map((doc) => doc.type))
    filled += BOX_TRUCK_DOC_TYPES.filter((type) => uploaded.has(type)).length
    const total = COMPLETENESS_TEXT_FIELDS.length + BOX_TRUCK_DOC_TYPES.length
    return Math.round((filled / total) * 100)
}

/**
 * Completeness for a driver's SELF-submitted verification packet — scored against only what that PATH actually
 * collects, not the full operator My-Drivers registration. A perfect MC-holder or no-MC submission reads 100%
 * (the generic `boxTruckRegistrationCompleteness` would score it ~32–42% and never turn `success`, making a
 * fully-compliant driver look two-thirds incomplete on the verification queues). Falls back to the generic score
 * for legacy/operator-created rows that carry no `verificationPath`.
 */
export function boxTruckVerificationCompleteness(info: BoxTruckVerificationInfo | undefined, path: BoxTruckVerificationPath | undefined): number {
    if (!path) return boxTruckRegistrationCompleteness(info)
    if (!info) return 0
    // Info fields each path actually collects. The mc_holder wizard has NO identity step (it skips NoMcInfoScreen),
    // so name/phone are only whatever the auth user happened to be seeded with — never guaranteed — and scoring
    // them would keep a perfectly-complete MC-holder packet below 100%. It guarantees only the MC + its two docs.
    // The no_mc wizard requires name + phone in its identity step, so those are scored.
    const infoFields: (keyof BoxTruckVerificationInfo)[] = path === 'mc_holder' ? ['mcNumber'] : ['name', 'phone']
    const requiredDocs = boxTruckVerificationDocs(path)
        .filter((slot) => slot.required)
        .map((slot) => slot.type)
    let filled = 0
    for (const key of infoFields) {
        const value = info[key]
        if (typeof value === 'string' && value.trim().length > 0) filled++
    }
    const uploaded = new Set((info.documents ?? []).map((doc) => doc.type))
    filled += requiredDocs.filter((type) => uploaded.has(type)).length
    const total = infoFields.length + requiredDocs.length
    return total === 0 ? 0 : Math.round((filled / total) * 100)
}
